/**
 * Flow Heatmap — strike × expiry grid for stable CBOE greeks tape.
 * MMEC shell: ThemeContext colors, semantic call/put colors, compact filter bar, intensity legend.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCBOEFieldValue, getFieldValue } from '../utils/dataModeUtils';
import { computeSpotGammaGexMme, getUnderlyingPriceFallback } from '../utils/gammaCalculations';
import { getFilterOptions, getUnderlyingSymbol, getExpiryFromRecord } from '../utils/filterUtils';
import { calculateMoneynessCategory } from '../utils/moneynessUtils';
import { dataService } from '../services/dataService';
import { DataMode } from '../types/DataMode';
import { ScannerData } from '../types/ScannerData';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import { readStablePrimarySessionFirst, readStableUnderlyingsSessionFirst } from '../utils/stableTabStorage';
import { mmecSimpleCloseBtnStyle, mmecSimpleFabStyle } from '../theme/mmecSimpleMobile';
import { ProToolFilterSlot } from './mmec/ProToolFilterSlot';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';
import './FlowHeatmap.ds.css';

/** Design System heat (preview flag only): direction is colour, strength is alpha at heat-1..5 over surface-2. */
const DS_HEAT_ALPHA = [0.18, 0.32, 0.5, 0.68, 0.85];
const DS_SURFACE_2 = '#1e222d';
const DS_UP_RGB = '38,166,154';
const DS_DOWN_RGB = '239,83,80';
const DS_FLOW_RGB = '179,136,255';
function dsHeatLevel(t: number): number {
  const u = Math.max(0, Math.min(1, t));
  if (u <= 0) return 0;
  return Math.max(1, Math.min(5, Math.ceil(u * 5)));
}
function dsHeatBg(rgb: string, t: number): string {
  const lvl = dsHeatLevel(t);
  return lvl === 0 ? DS_SURFACE_2 : `rgba(${rgb},${DS_HEAT_ALPHA[lvl - 1]})`;
}
function dsHeatFg(t: number): string {
  const lvl = dsHeatLevel(t);
  return lvl === 0 ? '#9598a1' : lvl >= 5 ? '#ffffff' : '#d1d4dc';
}

export type FlowHeatmapMetric = 'trades' | 'delta' | 'netPremium' | 'spotGamma' | 'flow' | 'premium' | 'openInterest' | 'volume' | 'totalVolume';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mixHex(a: string, b: string, t: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  if (!A || !B) return a;
  const u = Math.max(0, Math.min(1, t));
  const r = Math.round(A.r + (B.r - A.r) * u);
  const g = Math.round(A.g + (B.g - A.g) * u);
  const bl = Math.round(A.b + (B.b - A.b) * u);
  return `#${[r, g, bl].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/** Metrics whose cell values can be negative → signed heat (green vs red from theme). */
function isSignedMetric(m: FlowHeatmapMetric): boolean {
  return m === 'netPremium' || m === 'delta' || m === 'flow' || m === 'spotGamma';
}

function num(item: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = item[k];
    if (v === null || v === undefined || v === '' || v === '-') continue;
    const x = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(x)) return x;
  }
  return 0;
}

/** Best-effort tradable price for $-style heatmap metrics when Last is missing (common on stable tape). */
function effectiveOptionPx(row: Record<string, unknown>): number {
  const last = num(row, 'Last', 'last_price', 'lastPrice', 'Close', 'close');
  if (last > 0) return last;
  const mid = num(row, 'Mid', 'mid', 'Mark', 'mark');
  if (mid > 0) return mid;
  const theo = num(row, 'Theoretical Value', 'theoretical_value');
  if (theo > 0) return theo;
  const bid = num(row, 'Bid', 'bid');
  const ask = num(row, 'Ask', 'ask');
  if (bid > 0 && ask > 0) return (bid + ask) / 2;
  if (bid > 0) return bid;
  if (ask > 0) return ask;
  return 0;
}

function expiryStr(item: Record<string, unknown>): string {
  const v = item['Expiry Date'] ?? item.expiryDate ?? item.expiry_date ?? item.Expiry ?? '';
  return String(v || '').trim();
}

function side(item: Record<string, unknown>): 'C' | 'P' {
  const r = String(item['Option Type'] ?? item.optionType ?? '').toUpperCase();
  return r === 'C' || r === 'CALL' ? 'C' : 'P';
}

type MoneynessFilter = 'all' | 'ITM' | 'ATM' | 'OTM' | 'Unknown';

export interface FlowHeatmapProps {
  data?: ScannerData[];
  dataMode?: DataMode;
  hideFilters?: boolean;
  /** Simple shell: heatmap fills tab; metric + filters in left slide-out. */
  simpleLayout?: boolean;
}

const METRIC_OPTIONS: { value: FlowHeatmapMetric; labelEn: string; labelAr: string }[] = [
  { value: 'trades', labelEn: 'Trades', labelAr: 'الصفقات' },
  { value: 'delta', labelEn: 'Delta', labelAr: 'دلتا' },
  { value: 'netPremium', labelEn: 'Net Premium', labelAr: 'صافي العلاوة' },
  { value: 'spotGamma', labelEn: 'Spot Gamma', labelAr: 'سخيار بيع جاما' },
  { value: 'flow', labelEn: 'Flow', labelAr: 'التدفق' },
  { value: 'premium', labelEn: 'Premium', labelAr: 'العلاوة' },
  { value: 'openInterest', labelEn: 'Open Interest (C/P)', labelAr: 'الفائدة المفتوحة (م/ض)' },
  { value: 'volume', labelEn: 'Volume (C/P)', labelAr: 'الحجم (م/ض)' },
  { value: 'totalVolume', labelEn: 'Total Volume', labelAr: 'إجمالي الحجم' }
];

export const FlowHeatmap: React.FC<FlowHeatmapProps> = ({
  data: dataProp,
  dataMode = 'accumulated',
  hideFilters = false,
  simpleLayout = false,
}) => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const { openHistorical } = useHistoricalModal();
  const [metric, setMetric] = useState<FlowHeatmapMetric>('netPremium');
  const [selectedUnderlying, setSelectedUnderlying] = useState<string>(() => {
    const p = readStablePrimarySessionFirst();
    return p || 'all';
  });
  const [expiryFilter, setExpiryFilter] = useState<string>('all');
  const [sideFilter, setSideFilter] = useState<string>('all');
  const [moneynessFilter, setMoneynessFilter] = useState<MoneynessFilter>('all');
  const [cboeData, setCboeData] = useState<ScannerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setLoadFailed] = useState(false);
  const [underlyings, setUnderlyings] = useState<string[]>(() => {
    const list = readStableUnderlyingsSessionFirst();
    return list.length > 0 ? list : ['SPY'];
  });
  const [loadKey, setLoadKey] = useState(0);
  const hasLoadedOnceRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    const isBackgroundRefresh = hasLoadedOnceRef.current;
    if (!isBackgroundRefresh) setLoading(true);
    setLoadFailed(false);
    const load = async () => {
      try {
        const symbols = underlyings.length > 0 ? underlyings : ['SPY'];
        const expiryRange = localStorage.getItem('mmec_expiry_range') || 'weekly';
        const data = await dataService.fetchCboeGreeksData(symbols, { expiryRange });
        if (!cancelled) {
          setCboeData(data ?? []);
          setLoadFailed(!data?.length);
          hasLoadedOnceRef.current = true;
        }
      } catch (_) {
        if (!cancelled) {
          setCboeData([]);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [underlyings.join(','), loadKey]);

  // When stable fetch or strike/expiry change, refetch so data stays fresh. Do NOT reset user's
  // selections (underlying, metric, expiry, side, moneyness) — only update the data so values refresh.
  const [fetchVersion, setFetchVersion] = useState(0);
  useEffect(() => {
    const onFetch = () => {
      const list = readStableUnderlyingsSessionFirst();
      if (list.length > 0) {
        setUnderlyings(list);
        setSelectedUnderlying(list[0] || 'all');
      }
      setLoadKey((k) => k + 1);
    };
    const onStrikeOrExpiry = () => {
      setFetchVersion((v) => v + 1);
      setLoadKey((k) => k + 1);
    };
    window.addEventListener('stableDataFetched', onFetch);
    window.addEventListener('stableDataFetchRequested', onFetch);
    window.addEventListener('stableStrikeOrExpiryChanged', onStrikeOrExpiry);
    return () => {
      window.removeEventListener('stableDataFetched', onFetch);
      window.removeEventListener('stableDataFetchRequested', onFetch);
      window.removeEventListener('stableStrikeOrExpiryChanged', onStrikeOrExpiry);
    };
  }, []);

  const rawData = (dataProp?.length ? dataProp : cboeData) as unknown as Record<string, unknown>[];
  const filterOpts = useMemo(() => getFilterOptions((rawData || []) as unknown as ScannerData[]), [rawData, fetchVersion]);
  const availableUnderlyings = (filterOpts.availableUnderlyings?.length ? filterOpts.availableUnderlyings : underlyings) as string[];
  const availableExpiries = (filterOpts.availableExpiries ?? []) as string[];

  const data = useMemo(() => {
    if (!rawData?.length) return rawData;
    return rawData.filter((row) => {
      const underlying = getUnderlyingSymbol(row);
      const matchU = selectedUnderlying === 'all' || !selectedUnderlying || underlying === selectedUnderlying;
      const exp = getExpiryFromRecord(row) || expiryStr(row);
      const matchE = expiryFilter === 'all' || !expiryFilter || exp.includes(expiryFilter) || expiryFilter.includes(exp);
      const s = side(row);
      const matchS = sideFilter === 'all' || (sideFilter === 'C' && s === 'C') || (sideFilter === 'P' && s === 'P');
      const moneyness = calculateMoneynessCategory(row.Moneyness as number | string, row['Option Type'] as string);
      const matchM = moneynessFilter === 'all' || moneyness === moneynessFilter;
      return matchU && matchE && matchS && matchM;
    });
  }, [rawData, selectedUnderlying, expiryFilter, sideFilter, moneynessFilter, fetchVersion]);

  const underlyingPriceFallback = useMemo(() => getUnderlyingPriceFallback(data, dataMode), [data, dataMode]);

  const { strikes, expiries, grid, symbol } = useMemo(() => {
    const strikeSet = new Set<number>();
    const expirySet = new Set<string>();
    for (const row of data) {
      const st = num(row, 'Strike', 'Strike Price');
      const exp = expiryStr(row);
      if (st > 0) strikeSet.add(st);
      if (exp) expirySet.add(exp);
    }
    const strikes = Array.from(strikeSet).sort((a, b) => a - b);
    const expiries = Array.from(expirySet).sort();

    type Cell = { value: number; raw: number; calls: number; puts: number };
    const gridMap = new Map<string, Map<number, Cell>>();
    for (const exp of expiries) {
      gridMap.set(exp, new Map());
      for (const st of strikes) gridMap.get(exp)!.set(st, { value: 0, raw: 0, calls: 0, puts: 0 });
    }

    for (const row of data) {
      const st = num(row, 'Strike', 'Strike Price');
      const exp = expiryStr(row);
      if (!st || !exp || !gridMap.has(exp)) continue;
      const cellMap = gridMap.get(exp)!;
      if (!cellMap.has(st)) continue;
      const cur = cellMap.get(st)!;
      const s = side(row);

      if (metric === 'openInterest') {
        const oi = getCBOEFieldValue(row, 'Open Interest');
        if (s === 'C') cur.calls += oi;
        else cur.puts += oi;
        continue;
      }
      if (metric === 'volume') {
        const vol = getCBOEFieldValue(row, 'Volume');
        if (s === 'C') cur.calls += vol;
        else cur.puts += vol;
        continue;
      }
      if (metric === 'totalVolume') {
        cur.raw += getCBOEFieldValue(row, 'Volume');
        cur.value = cur.raw;
        continue;
      }

      let add = 0;
      if (metric === 'trades') {
        add = Number(getFieldValue(row, 'Trades', dataMode)) || 0;
      } else if (metric === 'delta') {
        add = getCBOEFieldValue(row, 'Delta');
      } else if (metric === 'netPremium') {
        const dp = num(row, 'Delta Premium', 'Delta Accumulated Premium', 'delta_premium', 'delta_accumulated_premium');
        const px = effectiveOptionPx(row);
        const deltaVal = getCBOEFieldValue(row, 'Delta');
        if (Number.isFinite(dp) && Math.abs(dp) > 0) add = dp;
        else if (px > 0) add = deltaVal * px * 100;
      } else if (metric === 'flow') {
        // Prefer bid/ask size imbalance (contracts / flow skew); else same $ proxy as net premium.
        const asym = num(
          row,
          'Delta ( ask - bid )',
          'Delta accumelated',
          'Delta accumulated',
          'delta_ask_bid'
        );
        if (Number.isFinite(asym) && Math.abs(asym) > 1e-12) {
          add = asym;
        } else {
          const dp = num(row, 'Delta Premium', 'Delta Accumulated Premium', 'delta_premium', 'delta_accumulated_premium');
          const px = effectiveOptionPx(row);
          const deltaVal = getCBOEFieldValue(row, 'Delta');
          if (Number.isFinite(dp) && Math.abs(dp) > 0) add = dp;
          else if (px > 0) add = deltaVal * px * 100;
        }
      } else if (metric === 'premium') {
        const bidP = num(row, 'Bid Volume Premium', 'Bid Accumulated Premium');
        const askP = num(row, 'Ask Volume Premium', 'Ask Accumulated Premium');
        if (bidP || askP) add = bidP + askP;
        else {
          const px = effectiveOptionPx(row);
          const vol = getCBOEFieldValue(row, 'Volume');
          add = px > 0 ? vol * px * 100 : 0;
        }
      } else if (metric === 'spotGamma') {
        const { spotGamma } = computeSpotGammaGexMme(row, dataMode, underlyingPriceFallback);
        add = spotGamma;
      }

      cur.raw += add;
      cur.value = cur.raw;
    }

    const grid: { expiry: string; strike: number; value: number; calls?: number; puts?: number }[] = [];
    for (const exp of expiries) {
      for (const st of strikes) {
        const c = gridMap.get(exp)!.get(st)!;
        grid.push({
          expiry: exp,
          strike: st,
          value: c.value,
          ...(metric === 'openInterest' || metric === 'volume' ? { calls: c.calls, puts: c.puts } : {})
        });
      }
    }

    const sym = data.length && (data[0]['Underlying'] ?? data[0]['underlying']) ? String(data[0]['Underlying'] ?? data[0]['underlying']) : underlyings[0] || '—';
    return { strikes, expiries, grid, symbol: sym };
  }, [data, dataMode, metric, underlyingPriceFallback, underlyings]);

  const isSplitMetric = metric === 'openInterest' || metric === 'volume';
  const { minVal, maxVal, maxCallsPuts } = useMemo(() => {
    let min = 0, max = 0, maxCP = 0;
    for (const c of grid) {
      if (isSplitMetric && 'calls' in c && 'puts' in c) {
        const callVal = c.calls ?? 0, putVal = c.puts ?? 0;
        if (callVal > maxCP) maxCP = callVal;
        if (putVal > maxCP) maxCP = putVal;
      } else {
        if (c.value < min) min = c.value;
        if (c.value > max) max = c.value;
      }
    }
    return { minVal: min, maxVal: max, maxCallsPuts: maxCP };
  }, [grid, isSplitMetric]);

  const ds = isDesignSystemOn();
  /** Theme-aligned heat scales (replaces legacy fixed RGB greens/reds). */
  const heatStyles = useMemo(() => {
    if (ds) {
      const signedT = (v: number) => (v > 0 ? v / Math.max(maxVal, 1e-12) : Math.abs(v) / Math.max(Math.abs(minVal), 1e-12));
      const bandT = (v: number) => (v - minVal) / Math.max(maxVal - minVal, 1e-12);
      const splitT = (v: number) => v / Math.max(maxCallsPuts, 1e-12);
      return {
        cellBg: (v: number) => (v === 0 ? DS_SURFACE_2 : isSignedMetric(metric) ? dsHeatBg(v > 0 ? DS_UP_RGB : DS_DOWN_RGB, signedT(v)) : dsHeatBg(DS_FLOW_RGB, bandT(v))),
        cellBgCalls: (v: number) => (v === 0 ? DS_SURFACE_2 : dsHeatBg(DS_UP_RGB, splitT(v))),
        cellBgPuts: (v: number) => (v === 0 ? DS_SURFACE_2 : dsHeatBg(DS_DOWN_RGB, splitT(v))),
        fgSingle: (v: number) => (v === 0 ? '#9598a1' : dsHeatFg(isSignedMetric(metric) ? signedT(v) : bandT(v))),
        fgSplit: (v: number) => (v === 0 ? '#9598a1' : dsHeatFg(splitT(v))),
      };
    }
    const low = colors.surfaceHover;
    const neutral = colors.sentimentNeutral;
    const pos = colors.chartSuccess;
    const neg = colors.chartError;
    const band = colors.chartPrimary;
    const callHi = colors.boughtCalls;
    const putHi = colors.boughtPuts;

    const cellBg = (value: number): string => {
      if (value === 0) return neutral;
      if (isSignedMetric(metric)) {
        if (value > 0) {
          const t = value / Math.max(maxVal, 1e-12);
          return mixHex(low, pos, Math.min(1, t));
        }
        const t = Math.abs(value) / Math.max(Math.abs(minVal), 1e-12);
        return mixHex(low, neg, Math.min(1, t));
      }
      const span = Math.max(maxVal - minVal, 1e-12);
      const t = (value - minVal) / span;
      return mixHex(low, band, Math.min(1, Math.max(0, t)));
    };

    const cellBgCalls = (value: number): string => {
      if (value === 0) return neutral;
      const t = value / Math.max(maxCallsPuts, 1e-12);
      return mixHex(low, callHi, Math.min(1, t));
    };

    const cellBgPuts = (value: number): string => {
      if (value === 0) return neutral;
      const t = value / Math.max(maxCallsPuts, 1e-12);
      return mixHex(low, putHi, Math.min(1, t));
    };

    const magSigned = Math.max(Math.abs(minVal), Math.abs(maxVal), 1);
    const fgSingle = (value: number): string => {
      if (value === 0) return colors.textMuted;
      if (isSignedMetric(metric)) {
        return Math.abs(value) / magSigned > 0.32 ? colors.textInverse : colors.text;
      }
      const span = Math.max(maxVal - minVal, 1e-12);
      const t = (value - minVal) / span;
      return t > 0.4 ? colors.textInverse : colors.text;
    };

    const fgSplit = (v: number): string =>
      v / Math.max(maxCallsPuts, 1e-12) > 0.32 ? colors.textInverse : colors.text;

    return { cellBg, cellBgCalls, cellBgPuts, fgSingle, fgSplit };
  }, [colors, minVal, maxVal, maxCallsPuts, metric, ds]);

  const formatCell = (value: number) => {
    if (!Number.isFinite(value)) return '—';
    if (metric === 'delta') {
      const a = Math.abs(value);
      if (a === 0) return '0.000';
      if (a >= 100) return value.toFixed(2);
      if (a >= 1) return value.toFixed(3);
      return value.toFixed(4);
    }
    if (metric === 'flow') {
      const a = Math.abs(value);
      if (a > 0 && a < 5000 && Math.abs(value - Math.round(value)) < 1e-6) {
        return `${Math.round(value)}`;
      }
    }
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value <= -1_000_000) return `-${(Math.abs(value) / 1_000_000).toFixed(2)}M`;
    if (value >= 1000 || value <= -1000) return `${(value / 1000).toFixed(2)}K`;
    if (Math.abs(value) < 50 && value !== 0) return value.toFixed(2);
    return value.toFixed(1);
  };

  const formatExpiry = (exp: string) => {
    if (exp.length >= 10) return exp.slice(0, 10);
    return exp;
  };

  const effectiveSymbol = selectedUnderlying !== 'all' ? selectedUnderlying : (availableUnderlyings[0] || 'SPY');
  const openHistoricalForCell = (strike: number, expiry: string, optionType: 'CALL' | 'PUT' = 'CALL') => {
    openHistorical(
      { underlying: effectiveSymbol, strike, expiry, optionType, source: 'volume' },
      simpleLayout ? { compactChrome: true } : undefined
    );
  };

  const [simpleDrawerOpen, setSimpleDrawerOpen] = useState(false);

  useEffect(() => {
    if (!simpleLayout || !simpleDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSimpleDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [simpleLayout, simpleDrawerOpen]);

  const isArabic = language === 'ar';
  const dir = isArabic ? 'rtl' : 'ltr';
  const selectStyle: React.CSSProperties = {
    height: 28,
    backgroundColor: 'var(--base)',
    color: 'var(--txt)',
    border: '1px solid var(--hair)',
    borderRadius: 8,
    padding: '0 8px',
    fontSize: 12
  };
  const stickyHeaderBg = 'var(--elev)';
  const cellBorder = '1px solid var(--hair)';
  const thStyle: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '.03em',
    color: 'var(--muted)',
    backgroundColor: stickyHeaderBg,
    border: cellBorder,
  };
  const numCellStyle: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  };
  const emptyCardStyle: React.CSSProperties = {
    padding: 24,
    background: 'var(--elev)',
    border: '1px solid var(--hair)',
    borderRadius: 10,
    color: 'var(--txt)',
  };
  const emptyTextStyle: React.CSSProperties = { margin: 0, fontSize: 13, color: 'var(--muted)', textAlign: 'center' };
  if (ds) {
    Object.assign(selectStyle, { height: 30, backgroundColor: 'var(--ground)', border: '1px solid var(--line)', borderRadius: 4, fontFamily: 'var(--font-sans)' });
    Object.assign(thStyle, {
      backgroundColor: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text-muted)',
      ...(isArabic ? { letterSpacing: 0 } : { textTransform: 'uppercase', letterSpacing: '.06em' }),
    });
    Object.assign(emptyCardStyle, { padding: 0, background: 'var(--ground-pane)', border: '1px solid var(--line)', borderRadius: 8 });
  }
  const dsCellBorder = ds ? '1px solid var(--line)' : cellBorder;
  /** Spot row (preview flag only): the strike nearest the underlying price, outlined in gold. */
  const spotStrike = (() => {
    if (!ds || selectedUnderlying === 'all' || !underlyingPriceFallback || underlyingPriceFallback === 100) return null;
    let best: number | null = null;
    for (const st of strikes) if (best == null || Math.abs(st - underlyingPriceFallback) < Math.abs(best - underlyingPriceFallback)) best = st;
    return best;
  })();

  if (loading && !(dataProp?.length)) {
    if (ds) {
      return (
        <div dir={dir} style={emptyCardStyle}>
          <ToolState kind="loading" lang={isArabic ? 'ar' : 'en'} />
        </div>
      );
    }
    return (
      <div dir={dir} style={emptyCardStyle}>
        <p style={emptyTextStyle}>{isArabic ? 'جاري التحميل…' : 'Loading…'}</p>
      </div>
    );
  }
  if (!data.length) {
    if (ds) {
      return (
        <div dir={dir} style={emptyCardStyle}>
          <ToolState kind="empty" lang={isArabic ? 'ar' : 'en'} title={isArabic ? 'لا توجد بيانات تطابق المرشحات.' : 'No data matches the selected filters.'} body="" />
        </div>
      );
    }
    return (
      <div dir={dir} style={emptyCardStyle}>
        <p style={emptyTextStyle}>{isArabic ? 'لا توجد بيانات تطابق المرشحات.' : 'No data matches the selected filters.'}</p>
      </div>
    );
  }

  const metricLabel = METRIC_OPTIONS.find((o) => o.value === metric);
  const subtitle = isArabic
    ? `صفوف = سعر التنفيذ، أعمدة = انتهاء الصلاحية. اللون = شدة ${metricLabel?.labelAr ?? ''} في كل تقاطع (شريط الجلب يحدد النطاق).`
    : `Rows = strike, columns = expiry. Color = ${metricLabel?.labelEn ?? 'metric'} intensity at each intersection (fetch bar sets range).`;

  const signedScaleFmt = (n: number) => {
    if (!Number.isFinite(n)) return '—';
    if (metric === 'delta') return n.toFixed(4);
    if (metric === 'flow' && Math.abs(n) < 1e6 && Math.abs(n - Math.round(n)) < 1e-3) return String(Math.round(n));
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)}K`;
    return n.toFixed(2);
  };

  const legendSigned = (
    <div className="flex flex-wrap items-center gap-3 mt-2" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
      <span>{isArabic ? 'دليل اللون:' : 'Legend:'}</span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? `rgba(${DS_DOWN_RGB},0.85)` : mixHex(colors.surfaceHover, colors.chartError, 0.85) }} />
        {isArabic ? 'سالب' : 'Negative'}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? DS_SURFACE_2 : colors.sentimentNeutral }} />
        {isArabic ? 'صفر' : 'Zero'}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? `rgba(${DS_UP_RGB},0.85)` : mixHex(colors.surfaceHover, colors.chartSuccess, 0.85) }} />
        {isArabic ? 'موجب' : 'Positive'}
      </span>
      <span className="ms-1 font-mono opacity-95" title={isArabic ? 'نطاق القيم في البيانات المعروضة' : 'Value range in current filtered data'}>
        {isArabic ? 'المقياس:' : 'Scale:'}{' '}
        {signedScaleFmt(minVal)} … {signedScaleFmt(maxVal)}
      </span>
      {metric === 'delta' && (
        <span className="max-w-md opacity-90">
          {isArabic
            ? '(Δ عشري؛ يُجمع عبر الفرص/العُقد في نفس سعر التنفيذ والانتهاء.)'
            : '(Δ as decimal; summed across calls/puts at same strike × expiry.)'}
        </span>
      )}
      {metric === 'flow' && (
        <span className="max-w-md opacity-90">
          {isArabic
            ? '(التدفق: عدم توازن دلتا الطلب/العرض إن وُجد؛ وإلا صافي العلاوة التقريبي.)'
            : '(Flow: bid/ask delta imbalance when present; else $ proxy like net premium.)'}
        </span>
      )}
    </div>
  );

  const legendUnsigned = (
    <div className="flex flex-wrap items-center gap-3 mt-2" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
      <span>{isArabic ? 'دليل اللون:' : 'Legend:'}</span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? DS_SURFACE_2 : colors.surfaceHover }} />
        {isArabic ? 'منخفض' : 'Low'}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-16 h-3 rounded-sm" style={{ background: ds ? `linear-gradient(90deg, rgba(${DS_FLOW_RGB},0.18), rgba(${DS_FLOW_RGB},0.85))` : `linear-gradient(90deg, ${colors.surfaceHover}, ${colors.chartPrimary})` }} />
        {isArabic ? '← عالي' : 'High →'}
      </span>
    </div>
  );

  const legendSplit = (
    <div className="flex flex-wrap items-center gap-3 mt-2" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
      <span>{isArabic ? 'دليل اللون:' : 'Legend:'}</span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? `rgba(${DS_UP_RGB},0.85)` : mixHex(colors.surfaceHover, colors.boughtCalls, 0.8) }} />
        {isArabic ? 'فرص' : 'Calls'}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ds ? `rgba(${DS_DOWN_RGB},0.85)` : mixHex(colors.surfaceHover, colors.boughtPuts, 0.8) }} />
        {isArabic ? 'خيارات بيع' : 'Puts'}
      </span>
    </div>
  );

  const filterFields = !hideFilters ? (
    <>
      <label className={`flex ${simpleLayout ? 'flex-col items-stretch' : 'items-center'} gap-2 text-xs shrink-0`}>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{isArabic ? 'المقياس' : 'Metric'}</span>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as FlowHeatmapMetric)}
          className={simpleLayout ? 'w-full' : 'min-w-[7rem]'}
          style={selectStyle}
          aria-label={isArabic ? 'المقياس' : 'Metric'}
        >
          {METRIC_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{isArabic ? opt.labelAr : opt.labelEn}</option>
          ))}
        </select>
      </label>
      <label className={`flex ${simpleLayout ? 'flex-col items-stretch' : 'items-center'} gap-2 text-xs shrink-0`}>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{isArabic ? 'الأصل' : 'Underlying'}</span>
        <select
          value={selectedUnderlying}
          onChange={(e) => setSelectedUnderlying(e.target.value)}
          className={simpleLayout ? 'w-full' : 'min-w-[5.5rem]'}
          style={selectStyle}
          aria-label={isArabic ? 'الأصل' : 'Underlying'}
        >
          <option value="all">{isArabic ? 'الكل' : 'All'}</option>
          {availableUnderlyings.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </label>
      <label className={`flex ${simpleLayout ? 'flex-col items-stretch' : 'items-center'} gap-2 text-xs shrink-0`}>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{isArabic ? 'انتهاء' : 'Expiry'}</span>
        <select
          value={expiryFilter}
          onChange={(e) => setExpiryFilter(e.target.value)}
          className={simpleLayout ? 'w-full' : 'min-w-[7rem]'}
          style={selectStyle}
          aria-label={isArabic ? 'انتهاء الصلاحية' : 'Expiry'}
        >
          <option value="all">{isArabic ? 'الكل' : 'All'}</option>
          {availableExpiries.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </label>
      <label className={`flex ${simpleLayout ? 'flex-col items-stretch' : 'items-center'} gap-2 text-xs shrink-0`}>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{isArabic ? 'الجانب' : 'Side'}</span>
        <select
          value={sideFilter}
          onChange={(e) => setSideFilter(e.target.value)}
          className={simpleLayout ? 'w-full' : 'min-w-[4.5rem]'}
          style={selectStyle}
          aria-label={isArabic ? 'الجانب' : 'Side'}
        >
          <option value="all">{isArabic ? 'الكل' : 'All'}</option>
          <option value="C">Call</option>
          <option value="P">Put</option>
        </select>
      </label>
      <label className={`flex ${simpleLayout ? 'flex-col items-stretch' : 'items-center'} gap-2 text-xs shrink-0`}>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{isArabic ? 'النقود' : 'Moneyness'}</span>
        <select
          value={moneynessFilter}
          onChange={(e) => setMoneynessFilter(e.target.value as MoneynessFilter)}
          className={simpleLayout ? 'w-full' : 'min-w-[5rem]'}
          style={selectStyle}
          aria-label={isArabic ? 'النقود' : 'Moneyness'}
        >
          <option value="all">{isArabic ? 'الكل' : 'All'}</option>
          <option value="ITM">ITM</option>
          <option value="ATM">ATM</option>
          <option value="OTM">OTM</option>
          <option value="Unknown">{isArabic ? 'غير معروف' : 'Unknown'}</option>
        </select>
      </label>
    </>
  ) : null;

  return (
    <div
      className={
        (simpleLayout
          ? 'relative flex flex-col flex-1 min-h-0 h-full p-2'
          : 'flex flex-col min-h-0') + (ds ? ' fhm-ds' : '')
      }
      dir={dir}
      style={
        simpleLayout
          ? { backgroundColor: 'var(--elev)', color: 'var(--txt)', fontFamily: 'var(--font-ui)' }
          : {
              backgroundColor: 'var(--elev)',
              border: '1px solid var(--hair)',
              borderRadius: ds ? 8 : 10,
              padding: 16,
              color: 'var(--txt)',
              fontFamily: 'var(--font-ui)',
            }
      }
    >
      {simpleLayout && !hideFilters && simpleDrawerOpen && (
        <button
          type="button"
          aria-label={isArabic ? 'إغلاق' : 'Close'}
          onClick={() => setSimpleDrawerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 120,
            border: 'none',
            margin: 0,
            padding: 0,
            background: 'rgba(0,0,0,0.45)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
        />
      )}
      {simpleLayout && !hideFilters && (
        <aside
          aria-hidden={!simpleDrawerOpen}
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            width: 'min(88vw, 320px)',
            zIndex: 130,
            backgroundColor: colors.surface,
            color: colors.text,
            borderRight: `1px solid ${colors.surfaceBorder}`,
            boxShadow: simpleDrawerOpen ? '8px 0 24px rgba(0,0,0,0.25)' : 'none',
            transform: simpleDrawerOpen ? 'translateX(0)' : 'translateX(-102%)',
            transition: 'transform 0.22s ease',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: simpleDrawerOpen ? 'auto' : 'none',
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: `1px solid ${colors.surfaceBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>{isArabic ? 'المرشحات' : 'Filters'}</span>
            <button
              type="button"
              onClick={() => setSimpleDrawerOpen(false)}
              style={{ ...mmecSimpleCloseBtnStyle, color: colors.textMuted }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div
            style={{
              overflow: 'auto',
              flex: 1,
              padding: 12,
              paddingLeft: `max(12px, env(safe-area-inset-left, 0px))`,
              paddingRight: `max(12px, env(safe-area-inset-right, 0px))`,
            }}
            className="flex flex-col gap-3"
          >
            {filterFields}
            <p className="text-xs leading-relaxed" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {subtitle}
            </p>
          </div>
        </aside>
      )}
      {simpleLayout && !hideFilters && (
        <button
          type="button"
          onClick={() => setSimpleDrawerOpen((o) => !o)}
          aria-expanded={simpleDrawerOpen}
          aria-label={isArabic ? 'فتح المرشحات' : 'Open filters'}
          style={mmecSimpleFabStyle({
            left: 8,
            top: '50%',
            primary: colors.primary,
            textInverse: colors.textInverse,
            border: colors.surfaceBorder,
          })}
        >
          ☰
        </button>
      )}
      <div className={`flex flex-wrap items-center gap-3 gap-y-2 mb-2 ${simpleLayout ? 'pl-14' : ''}`}>
        <h2 className={simpleLayout ? 'truncate' : ''} style={{ fontSize: simpleLayout ? 14 : 18, fontWeight: ds ? 700 : 600, margin: 0, color: 'var(--txt)' }}>
          {symbol} — {isArabic ? 'خريطة التدفق' : 'Flow Heatmap'}
        </h2>
      </div>
      {!simpleLayout && !hideFilters && (
        <ProToolFilterSlot>
          <div
            className="mb-2 flex flex-col gap-3"
            style={{ fontFamily: 'var(--font-ui, "IBM Plex Sans", sans-serif)' }}
          >
            <div className="flex flex-wrap gap-2 gap-y-2">{filterFields}</div>
            <p className="text-xs mb-0 leading-relaxed" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {subtitle}
            </p>
            <p className="text-xs mb-0" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {isArabic
                ? 'نطاق انتهاء الصلاحية وعدد أسعار التنفيذ من شريط الجلب أعلاه؛ شريط CBOE المستقر.'
                : 'Expiry range and strike depth come from the fetch bar above; stable CBOE greeks tape.'}
            </p>
          </div>
        </ProToolFilterSlot>
      )}

      {isSplitMetric ? legendSplit : isSignedMetric(metric) ? legendSigned : legendUnsigned}

      <div
        className={`overflow-x-auto overflow-y-auto flex-1 min-h-0 mt-2 ${simpleLayout ? 'max-h-none min-h-0' : 'max-h-[70vh]'}`}
        style={{ border: '1px solid var(--hair)', borderRadius: 8, ...(ds ? { background: 'var(--surface-2)' } : {}) }}
      >
        <table className="border-collapse w-full" style={{ minWidth: 280 }}>
          <thead>
            <tr>
              <th
                className="w-24 sticky start-0 top-0 z-20 text-start"
                style={thStyle}
              >
                {isArabic ? 'سعر التنفيذ' : 'Strike'}
              </th>
              {expiries.map((exp) => (
                <th
                  key={exp}
                  className="whitespace-nowrap text-center sticky top-0 z-10"
                  style={{ ...thStyle, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatExpiry(exp)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {strikes.map((strike) => (
              <tr key={strike} className={spotStrike === strike ? 'fhm-spot' : undefined}>
                <td
                  className="sticky start-0 z-10"
                  style={{
                    ...numCellStyle, padding: '6px 10px', fontSize: 12.5, backgroundColor: stickyHeaderBg, border: cellBorder, color: 'var(--txt)',
                    ...(ds ? { fontSize: 11, textAlign: 'end', backgroundColor: 'var(--surface-2)', border: dsCellBorder, color: spotStrike === strike ? 'var(--gold)' : 'var(--text-axis)', fontWeight: spotStrike === strike ? 700 : 600 } : {}),
                  }}
                >
                  {strike}
                </td>
                {expiries.map((exp) => {
                  const cell = grid.find((c) => c.strike === strike && c.expiry === exp);
                  const value = cell?.value ?? 0;
                  const calls = (cell && 'calls' in cell) ? (cell.calls ?? 0) : null;
                  const puts = (cell && 'puts' in cell) ? (cell.puts ?? 0) : null;
                  const isSplit = isSplitMetric && calls !== null && puts !== null;

                  if (isSplit) {
                    const callBg = heatStyles.cellBgCalls(calls);
                    const putBg = heatStyles.cellBgPuts(puts);
                    const callText = formatCell(calls);
                    const putText = formatCell(puts);
                    return (
                      <td
                        key={`${strike}-${exp}`}
                        className="p-0 text-center align-middle"
                        style={{ ...numCellStyle, fontSize: 12, minWidth: 96, border: dsCellBorder }}
                      >
                        <div className="flex w-full">
                          <div
                            role="button"
                            tabIndex={0}
                            className="flex-1 flex flex-col justify-center items-center py-1 cursor-pointer outline-offset-2 focus-visible:ring-2"
                            style={{
                              backgroundColor: callBg,
                              color: heatStyles.fgSplit(calls),
                              minWidth: 44,
                              borderInlineEnd: dsCellBorder
                            }}
                            title={isArabic ? 'فرص — انقر للرسم التاريخي' : 'Calls — click for historical chart'}
                            onClick={() => openHistoricalForCell(Number(strike), exp, 'CALL')}
                            onKeyDown={(e) => e.key === 'Enter' && openHistoricalForCell(Number(strike), exp, 'CALL')}
                          >
                            <span className="text-[10px] opacity-90">{isArabic ? 'م' : 'C'}</span>
                            <span>{callText}</span>
                          </div>
                          <div
                            role="button"
                            tabIndex={0}
                            className="flex-1 flex flex-col justify-center items-center py-1 cursor-pointer outline-offset-2 focus-visible:ring-2"
                            style={{
                              backgroundColor: putBg,
                              color: heatStyles.fgSplit(puts),
                              minWidth: 44
                            }}
                            title={isArabic ? 'خيارات بيع — انقر للرسم التاريخي' : 'Puts — click for historical chart'}
                            onClick={() => openHistoricalForCell(Number(strike), exp, 'PUT')}
                            onKeyDown={(e) => e.key === 'Enter' && openHistoricalForCell(Number(strike), exp, 'PUT')}
                          >
                            <span className="text-[10px] opacity-90">{isArabic ? 'ض' : 'P'}</span>
                            <span>{putText}</span>
                          </div>
                        </div>
                      </td>
                    );
                  }

                  const bg = heatStyles.cellBg(value);
                  return (
                    <td
                      key={`${strike}-${exp}`}
                      role="button"
                      tabIndex={0}
                      className="px-2 py-1 text-center whitespace-nowrap cursor-pointer outline-offset-2 focus-visible:ring-2"
                      style={{
                        ...numCellStyle,
                        fontSize: 12.5,
                        backgroundColor: bg,
                        color: heatStyles.fgSingle(value),
                        minWidth: 72,
                        border: dsCellBorder,
                        ...(ds ? { fontSize: 12 } : {})
                      }}
                      title={isArabic ? 'انقر للرسم التاريخي (افتراضي فرصة)' : 'Click for historical chart (default call)'}
                      onClick={() => openHistoricalForCell(Number(strike), exp, 'CALL')}
                      onKeyDown={(e) => e.key === 'Enter' && openHistoricalForCell(Number(strike), exp, 'CALL')}
                    >
                      {formatCell(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isSplitMetric && (
        <p className="text-xs mt-2" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
          {isArabic ? 'م = فرص، ض = خيارات البيع؛ كل خلية منقسمة حسب نوع الخيار.' : 'C = calls, P = puts; each cell is split by option type.'}
        </p>
      )}
      <p className="text-xs mt-1" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
        {isArabic
          ? 'مصدر البيانات: شريط CBOE المستقر. النقر على خلية يفتح الرسم التاريخي لذلك سعر التنفيذ والانتهاء.'
          : ds
            ? 'Source: stable greeks tape. Click a cell to open the historical chart for that strike and expiry.'
            : 'Source: stable CBOE greeks tape. Click a cell to open the historical chart for that strike and expiry.'}
      </p>
    </div>
  );
};

export default FlowHeatmap;
